/**
 * Broker Data Pipeline → a writer.
 *
 * One page per writer, routed by its name in the core's closed list. The cards are the load pipeline's writer cards
 * (`WriterActivityView`), handed this writer's counters; which counters show, and what they are called, is this page's
 * layout per writer. Below them, the window hour by hour, and what publish refused and why. A writer with no code yet
 * says so.
 */

import Box from '@mui/joy/Box'
import Link from '@mui/joy/Link'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { LoadPipelineWriterHour } from '@numeo/types-core'
import * as React from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { WriterActivityView, type WriterActivityLayout } from '../load-pipeline/components/WriterActivity'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, formatNumber, useLoadPipelineData } from '../load-pipeline/components/shared'
import { FmcsaRunPanel } from './components/FmcsaRunPanel'
import { NotYetBuilt, budgetLabel, budgetPeriod, budgetShare, writerTitle } from './components/shared'

interface BrokerWriterLayout {
    /** How the writer runs, in a sentence under the title. */
    about: string
    activity: WriterActivityLayout
    /** The hourly table's columns, left to right. */
    hourColumns: { counter: string; label: string }[]
}

/** W-FMCSA (ADR-047.2, ADR-047.6): a weekly run with its DAT step, a seed run once, and on-demand answers for one MC. */
const FMCSA: BrokerWriterLayout = {
    about: 'Reads FMCSA’s datasets once a week (Saturday and Sunday nights Eastern, and Run now below) and publishes what changed, with a weekly CONFIRM for brokerages in force; then looks each MC in force up on the DAT Directory, one at a time, and publishes its offices and profile. The seed states every MC with broker authority once. Another writer naming an MC the store does not know asks QCMobile on demand.',
    activity: {
        lastEventLabel: 'Last event',
        quietNote: 'No activity in this window. The run is weekly, so a window without its night is quiet.',
        showPlacesAndLag: false,
        rows: [
            [
                { counter: 'published', label: 'Published', hint: 'updates on brokerpipe-updates, every path', tone: 'success' },
                { counter: 'publishedState', label: 'STATE', hint: 'a seed, a change, or an on-demand answer' },
                { counter: 'publishedConfirm', label: 'CONFIRM', hint: 'the weekly confirmation of an unchanged brokerage in force' },
                { counter: 'unchanged', label: 'Unchanged', hint: 'same facts as last published: nothing sent' },
            ],
            [
                { counter: 'refused', label: 'Refused by publish', hint: 'the update failed the contract; reasons below', tone: 'danger' },
                { counter: 'quarantined', label: 'Quarantined', hint: 'an on-demand MC that is no brokerage, noted', tone: 'warning' },
                { counter: 'failed', label: 'Failed', hint: 'an MC the run could not read or publish; the next run states it again', tone: 'danger' },
            ],
            [
                { counter: 'runsCompleted_weekly', label: 'Weekly runs ok', hint: 'whole weekly runs that finished; samples count apart', tone: 'success' },
                { counter: 'runsFailed_weekly', label: 'Weekly runs failed', hint: 'a dataset unread, or too many failures', tone: 'danger' },
                { counter: 'published_weekly', label: 'Published by weekly runs', hint: 'FMCSA changes and confirmations, and DAT offices' },
                { counter: 'published_DAT_DIRECTORY', label: 'DAT Directory updates', hint: 'offices and profiles from the DAT step' },
            ],
            [
                { counter: 'runsCompleted_sample', label: 'Sample runs ok', hint: "runs on the register's first N MCs, to try it", tone: 'success' },
                { counter: 'runsFailed_sample', label: 'Sample runs failed', hint: 'a sample that stopped', tone: 'danger' },
                { counter: 'published_sample', label: 'Published by samples', hint: 'STATEs of the sampled MCs; never a CONFIRM' },
            ],
            [
                { counter: 'runsCompleted_seed', label: 'Seed runs ok', hint: 'one STATE per MC with broker authority', tone: 'success' },
                { counter: 'runsFailed_seed', label: 'Seed runs failed', hint: 'the seed stopped; it starts over', tone: 'danger' },
                { counter: 'published_seed', label: 'Published by the seed', hint: 'about 117,000 when it runs whole' },
            ],
            [
                { counter: 'onDemandAsked', label: 'On-demand asks', hint: 'one MC another writer named' },
                { counter: 'onDemand_PUBLISHED', label: 'Answered: published', hint: 'a brokerage the register had not named yet', tone: 'success' },
                { counter: 'onDemand_UNCHANGED', label: 'Answered: unchanged', hint: 'already published as QCMobile shows it' },
                { counter: 'onDemand_NOT_A_BROKER', label: 'Not a broker', hint: 'carrier authority only: noted', tone: 'warning' },
                { counter: 'onDemand_NO_SUCH_DOCKET', label: 'No such docket', hint: 'FMCSA does not know it: noted', tone: 'warning' },
                { counter: 'onDemand_FAILED', label: 'QCMobile down', hint: 'the asker got a 503 and retries', tone: 'danger' },
            ],
        ],
    },
    hourColumns: [
        { counter: 'published', label: 'Published' },
        { counter: 'publishedState', label: 'STATE' },
        { counter: 'publishedConfirm', label: 'CONFIRM' },
        { counter: 'unchanged', label: 'Unchanged' },
        { counter: 'refused', label: 'Refused' },
        { counter: 'quarantined', label: 'Quarantined' },
        { counter: 'runsCompleted', label: 'Runs ok' },
        { counter: 'runsFailed', label: 'Runs failed' },
        { counter: 'onDemandAsked', label: 'Asks' },
    ],
}

/** A built writer this page has no layout for yet: the counters every broker writer keeps (ADR-047 §3.3). */
const GENERIC: BrokerWriterLayout = {
    about: '',
    activity: {
        lastEventLabel: 'Last event',
        quietNote: 'No activity in this window.',
        showPlacesAndLag: false,
        rows: [
            [
                { counter: 'published', label: 'Published', hint: 'updates on brokerpipe-updates', tone: 'success' },
                { counter: 'unchanged', label: 'Unchanged', hint: 'same facts as last published: nothing sent' },
                { counter: 'refused', label: 'Refused by publish', hint: 'reasons below', tone: 'danger' },
                { counter: 'quarantined', label: 'Quarantined', hint: 'refused with a note', tone: 'warning' },
            ],
        ],
    },
    hourColumns: FMCSA.hourColumns.filter((column) => !column.counter.startsWith('onDemand')),
}

/** Writers with their own layout. A writer not yet built renders "not yet built" from its declaration instead. */
const LAYOUTS: Record<string, BrokerWriterLayout> = { FMCSA }

function HourlyTable({ hours, columns }: { hours: LoadPipelineWriterHour[]; columns: BrokerWriterLayout['hourColumns'] }) {
    // Newest first: the hour a reader asks about is usually the last one.
    const rows = [...hours].reverse()
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mb: 3, overflow: 'auto' }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
                Hour by hour
            </Typography>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                An hour with no event has no row. Times are your browser&apos;s.
            </Typography>
            <Table size="sm" stripe="odd">
                <thead>
                    <tr>
                        <th style={{ width: 170 }}>Hour</th>
                        {columns.map((column) => (
                            <th key={column.counter} style={{ textAlign: 'right' }}>
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((hour) => (
                        <tr key={hour.hourStart}>
                            <td>
                                {new Date(hour.hourStart).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </td>
                            {columns.map((column) => (
                                <td key={column.counter} style={{ textAlign: 'right' }}>
                                    {formatNumber(hour.counters[column.counter] ?? 0)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Sheet>
    )
}

export default function BrokerPipelineWriterPage() {
    const writer = (useParams<{ writer?: string }>().writer ?? '').toUpperCase()
    const layout = LAYOUTS[writer] ?? GENERIC
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getWriterActivity(writer, hours), [writer, hours])

    const declaration = data?.declaration
    const budget = declaration?.budget ?? null
    const share = budgetShare(budget, data?.budgetUse ?? null)
    const reasons = Object.entries(data?.totals.refusedByReason ?? {}).sort(([, a], [, b]) => b - a)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="h2">{writerTitle(writer)}</Typography>
                <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
            </Box>
            {layout.about ? (
                <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                    {layout.about}{' '}
                    <Link component={RouterLink} to={`/broker-pipeline/quarantine?writer=${writer}`}>
                        Its quarantine notes
                    </Link>
                    .
                </Typography>
            ) : null}

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {writer === 'FMCSA' ? <FmcsaRunPanel /> : null}

            {data && declaration && !declaration.built ? <NotYetBuilt declaration={declaration} /> : null}

            {data && declaration?.built ? (
                <>
                    <StatRow>
                        <Stat
                            label="Budget"
                            value={budget === null ? 'none declared' : budgetLabel(budget)}
                            hint={budget === null ? 'its ADR declares no budget' : `${declaration.adr ?? 'no ADR'}; the seed is declared apart`}
                        />
                        <Stat
                            label="Published of budget"
                            value={share === null ? '—' : `${Math.round(share * 100)}%`}
                            hint={
                                budget === null || !data.budgetUse
                                    ? 'nothing to compare'
                                    : `${formatNumber(data.budgetUse.published)} over ${budgetPeriod(budget)}, whatever the window above; over budget is read here, never paged`
                            }
                            tone={share !== null && share > 1 ? 'warning' : 'neutral'}
                        />
                    </StatRow>

                    <WriterActivityView data={data} layout={layout.activity} />

                    {reasons.length > 0 ? (
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mb: 3 }}>
                            <Typography level="title-md" sx={{ mb: 1 }}>
                                Refused by publish, by reason
                            </Typography>
                            <Table size="sm">
                                <tbody>
                                    {reasons.map(([reason, n]) => (
                                        <tr key={reason}>
                                            <td>
                                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                    {reason}
                                                </Typography>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(n)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    ) : null}

                    {data.hours.length > 0 ? <HourlyTable hours={data.hours} columns={layout.hourColumns} /> : null}
                </>
            ) : null}
        </Box>
    )
}
